import { useState } from "react";
import { Calendar } from "primereact/calendar";
import { addLocale, locale } from "primereact/api";

import {
    AR_CALENDAR_LOCALE,
    defaultBoundDate,
    formatLocalDate,
    formatLocalDateTime,
    parseLocalDateTime,
    withDefaultTime,
} from "utils/dateRange";
import { formatDate, formatDateTime } from "utils/dateFormat";

/**
 * An Arabic date+time field. Every date input in this dashboard is one of these.
 *
 * NOT `<input type="datetime-local">`, which is what these replaced. That
 * control's calendar popup is Chrome's own UI: it renders outside the page, so
 * no CSS or script from the app reaches it, and it follows the BROWSER's
 * language rather than the document's. On this dashboard that meant an Arabic,
 * right-to-left screen opening an English, left-to-right picker with "August
 * 2026" and "Today" in it. The field's own text could be nudged right with
 * `text-align`; the popup could not be touched at all.
 *
 * PICKING A DAY MEANS THE WHOLE DAY. Both this and the native control fill in
 * the current clock time the moment a date is chosen, so choosing 01/05 → 31/05
 * at 14:20 quietly asked for "14:20 on the 1st through 14:20 on the 31st" and
 * dropped the morning of the first day and most of the last. `withDefaultTime`
 * snaps a newly picked day to 00:00 or 23:59 — but only when the DAY changed,
 * so an operator narrowing to one afternoon keeps the time they typed. An empty
 * field also OPENS on that time rather than on the current clock, so the value
 * never appears to jump once a day is clicked.
 *
 * Values are the plain wall-clock string "YYYY-MM-DDTHH:mm" throughout — never
 * an ISO instant. The API compares these against `orders.createdAt`, a bare
 * MySQL datetime with no zone attached, so converting through UTC on the way
 * out and back is a round trip that can only lose.
 *
 * @param {object} props
 * @param {string} props.value - "YYYY-MM-DDTHH:mm", or "" when unset.
 * @param {Function} props.onChange - Receives the next value in the same shape.
 * @param {"start"|"end"} [props.edge="start"] - Which end of a range this is.
 * @param {boolean} [props.dateOnly=false] - Store and emit "YYYY-MM-DD" with no
 *   time part, for fields whose value IS a day (a delivery offer's start and end
 *   dates are stored in DATE columns and compared as plain strings). Keeping
 *   their shape exactly is what lets those screens share this component without
 *   changing what they send.
 * @param {string} [props.placeholder]
 * @param {string} [props.inputId] - For a label's htmlFor.
 */
const DateTimeField = ({
    value,
    onChange,
    edge = "start",
    dateOnly = false,
    placeholder = "اختر التاريخ",
    inputId,
    ...rest
}) => {
    const parsed = parseLocalDateTime(value);

    // Only while the field is EMPTY. With a value present PrimeReact manages
    // its own view, and taking that over would break month navigation.
    const [viewDate, setViewDate] = useState(() => defaultBoundDate(edge));
    const emptyFieldView = parsed
        ? {}
        : { viewDate, onViewDateChange: (event) => setViewDate(event.value) };

    return (
        <Calendar
            inputId={inputId}
            value={parsed}
            {...emptyFieldView}
            onChange={(event) => {
                if (dateOnly) {
                    onChange(formatLocalDate(event.value));
                    return;
                }

                const formatted = formatLocalDateTime(event.value);
                onChange(formatted ? withDefaultTime(formatted, value, edge) : "");
            }}
            dateFormat="dd/mm/yy"
            showTime={!dateOnly}
            // The dashboard's one format (utils/dateFormat.js): the field
            // reads «01/10/2026, 2:05 PM», the same text as a table cell.
            hourFormat="12"
            formatDateTime={dateOnly ? formatDate : formatDateTime}
            showButtonBar
            readOnlyInput
            // No trigger button: the field opens the picker when clicked, so a
            // separate icon beside it is one more thing to line up and nothing
            // to gain.
            placeholder={placeholder}
            className="w-100"
            // Rendered at the body, not inline. Inside a Bootstrap modal — the
            // delivery-offer form and the statement modal are both modals — an
            // inline panel is clipped by the modal's own overflow and simply
            // never appears, so the field looked dead when clicked. The z-index
            // that puts it above the modal is in index.scss.
            appendTo={typeof document === "undefined" ? undefined : document.body}
            panelClassName="date-filter__panel"
            {...rest}
        />
    );
};

// Registered once at module load; PrimeReact keeps locales in a module-level
// registry, so repeating this per render would be wasted work.
addLocale("ar", AR_CALENDAR_LOCALE);
locale("ar");

export default DateTimeField;
